/**
 * "5 minutes ago" / "in 2 hours" / "yesterday" in `locale`, switching to a
 * short date ("12 Sep") once the time is a week or more away. Past and future
 * both work. With `justNow`, anything less than a minute old shows that text
 * instead, and so does a time slightly in the future: a timestamp the server
 * just wrote can read ahead of a browser clock that runs a little slow.
 * Returns "" for an unparseable timestamp.
 */
export function formatRelativeTime(
	iso: string,
	locale: string,
	justNow?: string,
): string {
	const ts = new Date(iso).getTime();
	if (!Number.isFinite(ts)) return "";
	const deltaMs = ts - Date.now();
	if (justNow !== undefined && deltaMs > -60_000 && deltaMs < 60_000) {
		return justNow;
	}
	const minutes = Math.round(deltaMs / 60_000);
	const hours = Math.round(minutes / 60);
	const days = Math.round(hours / 24);
	try {
		if (Math.abs(days) >= 7) {
			return new Date(ts).toLocaleDateString(locale, {
				day: "numeric",
				month: "short",
			});
		}
		const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
		if (Math.abs(minutes) < 60) return rtf.format(minutes, "minute");
		if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
		return rtf.format(days, "day");
	} catch {
		// An unsupported locale tag throws a RangeError.
		return new Date(ts).toLocaleDateString();
	}
}
